import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { io, Socket } from 'socket.io-client'
import toast from 'react-hot-toast'

import { useAuth } from './AuthContext'
import { Message } from '@/types'

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
}

const SocketContext = createContext<SocketContextType | undefined>(undefined)

interface SocketProviderProps {
  children: ReactNode
}

export function SocketProvider({ children }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const { user } = useAuth()

  useEffect(() => {
    if (user) {
      const socketInstance = io(import.meta.env.VITE_SOCKET_URL || 'http://localhost:3001', {
        auth: {
          token: localStorage.getItem('auth_token')
        }
      })

      socketInstance.on('connect', () => {
        console.log('Socket connected')
        setIsConnected(true)
        
        // Join store room
        socketInstance.emit('join-store', user.storeId)
      })

      socketInstance.on('disconnect', () => {
        console.log('Socket disconnected')
        setIsConnected(false)
      })

      socketInstance.on('connect_error', (error) => {
        console.error('Socket connection error:', error)
        setIsConnected(false)
      })

      // Listen for new messages
      socketInstance.on('new-message', (data: { conversationId: string; message: Message }) => {
        console.log('New message received:', data)
        
        // Show notification for inbound messages
        if (data.message.direction === 'INBOUND') {
          toast.success('New message received!')
        }
        
        // Trigger refetch of conversations/messages
        // This would be handled by React Query invalidation
        window.dispatchEvent(new CustomEvent('new-message', { detail: data }))
      })

      // Listen for conversation updates
      socketInstance.on('conversation-updated', (data: { conversationId: string; status: string; assignedAgentId?: string }) => {
        console.log('Conversation updated:', data)
        window.dispatchEvent(new CustomEvent('conversation-updated', { detail: data }))
      })

      // Listen for conversation assignment
      socketInstance.on('conversation-assigned', (data: { conversationId: string; agentId: string; agent: any }) => {
        console.log('Conversation assigned:', data)
        if (data.agentId === user.id) {
          toast.success('New conversation assigned to you!')
        }
        window.dispatchEvent(new CustomEvent('conversation-assigned', { detail: data }))
      })

      // Listen for conversation closure
      socketInstance.on('conversation-closed', (data: { conversationId: string }) => {
        console.log('Conversation closed:', data)
        window.dispatchEvent(new CustomEvent('conversation-closed', { detail: data }))
      })

      // Listen for messages read
      socketInstance.on('messages-read', (data: { conversationId: string }) => {
        console.log('Messages read:', data)
        window.dispatchEvent(new CustomEvent('messages-read', { detail: data }))
      })

      setSocket(socketInstance)

      return () => {
        socketInstance.emit('leave-store', user.storeId)
        socketInstance.disconnect()
        setSocket(null)
        setIsConnected(false)
      }
    }
  }, [user])

  const value = {
    socket,
    isConnected
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}

export function useSocket() {
  const context = useContext(SocketContext)
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}